import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  id: "custom_input_id_desc",
  class: "sr-only"
}
const _hoisted_2 = { class: "q-ma-none q-pa-none" }
const _hoisted_3 = { class: "q-pa-none q-ma-none" }
const _hoisted_4 = {
  key: 0,
  class: "q-mb-sm q-mx-sm"
}

import {
    ref,
    toRefs,
    computed,
    reactive,
    onBeforeMount,
    watch
} from 'vue'
import { useFocus } from '@vueuse/core';
import { QInput, useQuasar, ValidationRule } from 'quasar';

// Personal components
import QProgressBar from '@components/QProgressBar.vue'
import DiagGeneric from '@components/dialog/DiagGeneric.vue'

// Stores
import { useUiStore } from '@stores/ui'

// Personal libs
import { $t } from 'src/i18n/utils';
import { checkEmail, checkPassword, checkUsername } from '@shared/lib/security.lib';
import { NotificationTypeEnum } from '@shared/enum/notification.enum';
import { apiPostPassSecurityLevel } from '@data/datasource/remote/password.api';
import { emailRules, messageRules, passwordRules, usernameRules } from '@shared/lib/form.lib';
import { IQDialogPromptOption } from '@shared/interface/qDialogTemplate.interface';
import { IConfig } from '@shared/interface/config.interface';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';
import { SecretTypeEnum } from '@shared/enum/secret-type.enum';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';

/**
 * Interfaces
 */
type EnterkeyhintType = 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send' | undefined
type InputType = 'text' | 'password' | 'number' | 'email' | 'textarea'
interface IData {
    type?: InputType
    icon?: 'person' | 'mail' | 'lock'
    label?: string
    rule?: {
        btnLabel?: string
        label?: string
        desc?: string
        validationRules?: ValidationRule[]
    }
    example?: {
        label?: string
        content?: string
    }
    autocomplete?: 'email' | 'current-password' | 'username'
    valid?: boolean
    pattern?: string
    securityLevelLabel?: string

    // For textarea only
    rows?: number;
    maxRows?: number;

    btnClearLabel?: string
    btnToggle?: {
        labelShow?: string
        labelHide?: string
    }
}

/**
 * Constants
 */
const apiPostPassSecurityLevelTimeoutThreshold = 300

/**
 * HTMLElement refs
 */
interface QInputProps {
    // Model
    modelValue?: string

    // Content
    placeholder?: string
    ariaDescribedBy?: string
    showAriaDescription?: boolean
    inputType?: InputFiedTypeEnum
    secretType?: SecretTypeEnum
    enterkeyhint?: EnterkeyhintType
    inputPrompt?: IQDialogPromptOption

    // Style
    extraClass?: string;
    dense?: boolean

    // State
    disable?: boolean
    required?: boolean
    enablePassChecker?: boolean
   
    // Actions
    onEnterKey?: (...args: any[]) => void;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'QCustomInput',
  props: {
    modelValue: {},
    placeholder: { default: undefined },
    ariaDescribedBy: {},
    showAriaDescription: { type: Boolean, default: false },
    inputType: { default: InputFiedTypeEnum.EMAIL },
    secretType: { default: SecretTypeEnum.NORMAL },
    enterkeyhint: { default: 'done' },
    inputPrompt: {},
    extraClass: {},
    dense: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    required: { type: Boolean, default: true },
    enablePassChecker: { type: Boolean, default: false },
    onEnterKey: { type: Function, default: undefined }
  },
  emits: ['update:modelValue'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

// Vendor
const customQInput = ref<QInput>();
const { focused: customQInputFocused } = useFocus(customQInput);

/**
 * Globals
 */
const $q = useQuasar()

/**
 * Stores
 */
const uiStore = useUiStore()

/**
 * Events
 */
const emit = __emit

/**
 * Properties
 */
const props = __props;
const {
    onEnterKey,
    required,
    placeholder,
    ariaDescribedBy,
    inputType,
    extraClass,
    disable,
    dense,
    modelValue,
    enablePassChecker,
    secretType,
    enterkeyhint,
    inputPrompt
} = toRefs(props);

/**
 * Reactive data
 */
const showRules = ref<boolean>(false)
const isPasswordOrSecretCode = ref<boolean | undefined>(true);
const apiPostPassSecurityLevelTimeout = ref<NodeJS.Timeout | undefined>(undefined)
const passSecurityLevel = ref<number | undefined>(undefined)
// Main datas
const data = reactive<IData>({})

/**
 * Computed properties
 */
const storeConfig = computed<IConfig | undefined>(() => uiStore.config)
const computedType = computed<InputType | undefined>(() => {
    if (InputFiedTypeEnum.PASSWORD === inputType.value) {
        if (isPasswordOrSecretCode.value || disable.value) {
            return data.type
        } else {
            return 'text'
        }
    } else {
        return data.type
    }
})

/**
 * Methods
 */
function checkData(data: string): boolean {
    if (inputType.value === InputFiedTypeEnum.EMAIL) {
        return checkEmail(data)
    } else if (inputType.value === InputFiedTypeEnum.PASSWORD) {
        return checkPassword(data)
    } else if (inputType.value === InputFiedTypeEnum.USERNAME) {
        return checkUsername(data)
    } else {
        return false
    }
}

function setData() {
    const secretTypeConverted = SecretTypeEnum.valueOf(secretType.value) ?? SecretTypeEnum.NORMAL
    if (inputType.value === InputFiedTypeEnum.EMAIL) {
        Object.assign(data, {
            type: 'email',
            icon: 'mail',
            label: `${required.value || inputPrompt.value?.required ? '* ' : ''}${inputPrompt.value?.label ?? $t('form.email.label')} :`,
            rule: {
                btnLabel: $t('form.email.rules.btnLabel'),
                label: $t('form.email.rules.label'),
                desc: $t('form.email.rules.desc'),
                validationRules: emailRules
            },
            example: {
                label: $t('form.email.example.label'),
                content: $t('form.email.example.content')
            },
            autocomplete: 'email',
            btnClearLabel: $t('form.email.reset'),
        } as IData)
    } else if (inputType.value === InputFiedTypeEnum.PASSWORD) {
        Object.assign(data, {
            type: 'password',
            icon: 'lock',
            label: `${required.value || inputPrompt.value?.required ? '* ' : ''}${inputPrompt.value?.label ?? $t(`form.password.${secretTypeConverted}.label`)} :`,
            rule: {
                btnLabel: $t('form.password.rules.btnLabel'),
                label: $t('form.password.rules.label'),
                desc: $t('form.password.rules.desc', {
                    passwordMin: storeConfig.value?.security?.password?.min ?? 0,
                    passwordMax: storeConfig.value?.security?.password?.max ?? 0,
                    passwordAuthCar: storeConfig.value?.security?.password?.authCar ?? '*-',
                }),
                validationRules: passwordRules
            },
            example: {
                label: $t('form.password.example.label'),
                content: $t('form.password.example.content')
            },
            autocomplete: 'current-password',
            securityLevelLabel: $t('form.password.securityLevelLabel'),
            btnClearLabel: $t(`form.password.${secretTypeConverted}.reset`),
            btnToggle: {
                labelHide: $t(`form.password.${secretTypeConverted}.hide`),
                labelShow: $t(`form.password.${secretTypeConverted}.show`),
            }
        } as IData)
    } else if (inputType.value === InputFiedTypeEnum.USERNAME) {
        Object.assign(data, {
            type: 'text',
            icon: 'person',
            label: `${required.value || inputPrompt.value?.required ? '* ' : ''}${inputPrompt.value?.label ?? $t('form.username.label')} :`,
            rule: {
                btnLabel: $t('form.username.rules.btnLabel'),
                label: $t('form.username.rules.label'),
                desc: $t('form.username.rules.desc', {
                    usernameMin: storeConfig.value?.security?.username?.min ?? 0,
                    usernameMax: storeConfig.value?.security?.username?.max ?? 0
                }),
                validationRules: usernameRules
            },
            example: {
                label: $t('form.username.example.label'),
                content: $t('form.username.example.content')
            },
            autocomplete: 'username',
            btnClearLabel: $t('form.username.reset')
        } as IData)
    } else if (inputType.value === InputFiedTypeEnum.TEXTAREA) {
        Object.assign(data, {
            type: 'textarea',
            label: `${required.value || inputPrompt.value?.required ? '* ' : ''}${inputPrompt.value?.label ?? $t('form.textarea.label', { messageMin: uiStore.config?.security?.message?.min ?? 6})} :`,
            rule: {
                btnLabel: $t('form.textarea.rules.btnLabel'),
                label: $t('form.textarea.rules.label'),
                desc: $t('form.textarea.rules.desc', {
                    messageMin: storeConfig.value?.security?.message?.min ?? 0
                }),
                validationRules: messageRules
            },
            example: {
                label: $t('form.textarea.example.label'),
                content: $t('form.textarea.example.content')
            },
            rows: 3,
            maxRows: 6,
            btnClearLabel: $t('form.textarea.reset')
        } as IData)
    } else {
        Object.assign(data, {} as IData)
    }
}

/**
 * Methods
 */
async function onModelChange() {
    if (enablePassChecker.value && InputFiedTypeEnum.PASSWORD === inputType.value) {
        clearTimeout(apiPostPassSecurityLevelTimeout.value)

        apiPostPassSecurityLevelTimeout.value = setTimeout(async () => {
            await apiPostPassSecurityLevel({readablePassword: modelValue.value ?? ''})
                .then(response => {
                    passSecurityLevel.value = response.returnSuccess?.score ?? 0
                })
                .catch(() => {
                    passSecurityLevel.value = 0
                })
        }, apiPostPassSecurityLevelTimeoutThreshold)
    }
}

function clearUsername() {
    customQInput.value?.resetValidation();
    emit('update:modelValue', undefined)
    focus();
}

function hasError(): boolean {
    return customQInput.value?.hasError === true
}

function validate() {
    customQInput.value?.validate()
}

function resetValidation() {
    customQInput.value?.resetValidation()
}

function focus() {
    customQInputFocused.value = true
}

function blur() {
    customQInput.value?.blur()
}

/**
 * Watch values
 */
watch(() => modelValue.value, () => {
    onModelChange()
}, { immediate: true })

/**
 * Hooks
 */
onBeforeMount(() => {
    setData()
    data.valid = false
})

/**
 * Exposed methods
 */
__expose({
    clearUsername,
    hasError,
    validate,
    resetValidation,
    focus,
    blur
})

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_icon = _resolveComponent("q-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("p", _hoisted_2, _toDisplayString(data.rule?.label ?? '') + " : " + _toDisplayString(data.rule?.desc ?? ''), 1 /* TEXT */),
        _createElementVNode("p", _hoisted_3, _toDisplayString(data.example?.label ?? '') + " " + _toDisplayString(data.example?.content ?? ''), 1 /* TEXT */)
      ]),
      _createVNode(_unref(QInput), {
        "model-value": _unref(modelValue),
        "onUpdate:modelValue": _cache[3] || (_cache[3] = (newValue) => emit('update:modelValue', newValue)),
        type: computedType.value,
        dense: _unref(dense),
        placeholder: _unref(placeholder),
        "aria-required": _unref(required),
        "aria-describedby": _unref(ariaDescribedBy) ?? 'custom_input_id_desc',
        "aria-invalid": checkData(_unref(modelValue) ?? ''),
        "aria-disable": _unref(disable),
        disable: _unref(disable),
        label: data.label,
        pattern: data.pattern,
        autocomplete: data.autocomplete,
        "stack-label": _unref(inputPrompt)?.stackLabel,
        "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
        "label-color": _unref($q).dark.isActive ? 'white' : 'black',
        class: _normalizeClass(['q-mb-sm', _unref(extraClass), 'q-ml-none', 'q-pl-none']),
        "input-class": {
                'text-white': _unref($q).dark.isActive,
                'text-black': !_unref($q).dark.isActive,
                'set-outline-input q-ml-none q-pl-none': true,
                'dense': _unref(dense)
            },
        rows: data.rows,
        "max-rows": data.maxRows,
        rules: data.rule?.validationRules,
        enterkeyhint: _unref(enterkeyhint),
        ref_key: "customQInput",
        ref: customQInput,
        outlined: "",
        "hide-bottom-space": "",
        "lazy-rules": "",
        onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (_unref(onEnterKey) != null ? _unref(onEnterKey)() : undefined), ["enter"]))
      }, {
        prepend: _withCtx(() => [
          _createVNode(_component_q_btn, {
            flat: "",
            round: "",
            dense: "",
            icon: "help",
            class: "set-outline",
            color: "primary",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showRules.value = true))
          }),
          (data.icon)
            ? (_openBlock(), _createBlock(_component_q_icon, {
                key: 0,
                name: data.icon
              }, null, 8 /* PROPS */, ["name"]))
            : _createCommentVNode("v-if", true)
        ]),
        append: _withCtx(() => [
          _createCommentVNode(" Clear button "),
          _createVNode(_component_q_btn, {
            flat: "",
            round: "",
            dense: "",
            icon: "cancel",
            class: "set-outline",
            color: "primary",
            disable: _unref(disable),
            title: data.btnClearLabel,
            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (clearUsername()), ["stop"]))
          }, null, 8 /* PROPS */, ["disable", "title"]),
          _createCommentVNode(" Secret display button "),
          (_unref(InputFiedTypeEnum).PASSWORD === _unref(inputType))
            ? (_openBlock(), _createBlock(_component_q_btn, {
                key: 0,
                flat: "",
                dense: "",
                round: "",
                icon: isPasswordOrSecretCode.value ? 'visibility_off' : 'visibility',
                class: "set-outline",
                color: "primary",
                title: isPasswordOrSecretCode.value ? data.btnToggle?.labelShow : data.btnToggle?.labelHide,
                "aria-pressed": !isPasswordOrSecretCode.value,
                disable: _unref(disable),
                onClick: _cache[2] || (_cache[2] = ($event: any) => (isPasswordOrSecretCode.value = !isPasswordOrSecretCode.value))
              }, null, 8 /* PROPS */, ["icon", "title", "aria-pressed", "disable"]))
            : _createCommentVNode("v-if", true)
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["model-value", "type", "dense", "placeholder", "aria-required", "aria-describedby", "aria-invalid", "aria-disable", "disable", "label", "pattern", "autocomplete", "stack-label", "bg-color", "label-color", "class", "input-class", "rows", "max-rows", "rules", "enterkeyhint"])
    ]),
    (_unref(enablePassChecker))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(QProgressBar, {
            "pb-description": data.securityLevelLabel ?? '',
            "pb-show-description": true,
            "pb-current-value": passSecurityLevel.value ?? 0,
            "pb-max": 4,
            "is-password-checker": true,
            dense: _unref(dense)
          }, null, 8 /* PROPS */, ["pb-description", "pb-current-value", "dense"])
        ]))
      : _createCommentVNode("v-if", true),
    _createVNode(DiagGeneric, {
      show: showRules.value,
      title: data.rule?.label,
      message: `
            ${data.rule?.desc}<br><br>
            ${data.example?.label}: ${data.example?.content}
        `,
      "notification-type": _unref(NotificationTypeEnum).INFO,
      html: true,
      "hide-cancel": true,
      "hide-valid": true,
      "dialog-size": _unref(DialogSizeEnum).EXTRA_SMALL,
      onCancel: _cache[5] || (_cache[5] = ($event: any) => (showRules.value = false)),
      onClose: _cache[6] || (_cache[6] = ($event: any) => (showRules.value = false)),
      onValid: _cache[7] || (_cache[7] = ($event: any) => (showRules.value = false))
    }, null, 8 /* PROPS */, ["show", "title", "message", "notification-type", "dialog-size"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})